import React, { useMemo } from 'react';

import { Chip, IconButton, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import { format, isPast } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import InputWithDebouncedAction from 'components/InputWithDebouncedAction/InputWithDebouncedAction';
import Table from 'components/Table';
import type { DsarCase } from 'config/api/dsar/dsar.types';
import dsar_messages from 'messages/dsar_messages';
import general_messages from 'messages/general_messages';
import search_messages from 'messages/search_messages';
import PATHS from 'router/PATHS';
import type { TableDataMappingRow } from 'types/Table';

import useStyles from '../DsarCasesPage.styles';

type Props = {
  data: DsarCase[];
  filtersService?: any;
  loading: boolean;
  sort: { key: string | null; direction: string | null };
  onSort: (key: string, direction: string) => void;
  onSearch: (searchTerm: string) => void;
  onEdit: (dsarCase: DsarCase) => void;
  onOpen: (dsarCase: DsarCase) => void;
  searchValue: string;
};

const formatDate = (value?: string | null) => (value ? format(new Date(value), 'yyyy-MM-dd') : '-');

const buildDetailPath = (id: number) => PATHS.DSAR_CASES_DETAIL.replace(':id', String(id));

const getStatusIcon = (code: string, isOverdue: boolean, classNames: ReturnType<typeof useStyles>) => {
  if (isOverdue) return <WarningIcon aria-hidden='true' className={`${classNames.statusIcon} ${classNames.overdueIcon}`} />;
  if (code === 'completed' || code === 'closed') {
    return <CheckCircleIcon aria-hidden='true' className={`${classNames.statusIcon} ${classNames.activeIcon}`} />;
  }
  if (code === 'in_progress' || code === 'distributed') {
    return <RemoveCircleOutlineIcon aria-hidden='true' className={`${classNames.statusIcon} ${classNames.inProgressIcon}`} />;
  }
  return <CancelIcon aria-hidden='true' className={`${classNames.statusIcon} ${classNames.inactiveIcon}`} />;
};

const DsarCasesTable: React.FC<Props> = ({ data, filtersService, loading, sort, onSort, onSearch, onEdit, onOpen, searchValue }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const getRowHref = React.useCallback((dsarCase: DsarCase) => buildDetailPath(dsarCase.id), []);

  const dataMapping: TableDataMappingRow<DsarCase>[] = useMemo(
    () => [
      {
        id: 'caseReference',
        width: '12%',
        label: t(dsar_messages.cases.table.case_reference),
        get: dsarCase => <span className={styles.noWrap}>{dsarCase.caseReference}</span>,
      },
      {
        id: 'requesterName',
        width: '16%',
        label: t(dsar_messages.cases.table.requester),
        get: dsarCase => (
          <div className={styles.stackedCell}>
            <span>{dsarCase.requesterName}</span>
            <Typography className={styles.mutedText} variant='caption'>
              {dsarCase.requesterEmail}
            </Typography>
          </div>
        ),
      },
      {
        id: 'requestTypes',
        width: '16%',
        blockSorting: true,
        label: t(dsar_messages.cases.table.request_types),
        get: dsarCase => (
          <div className={styles.requestTypes}>
            {dsarCase.requestTypes.length ? (
              dsarCase.requestTypes.map(({ id, requestType }) => (
                <Chip key={id} className={styles.requestTypeChip} label={requestType.name} size='small' />
              ))
            ) : (
              <span>{t(general_messages.no_data)}</span>
            )}
          </div>
        ),
      },
      {
        id: 'status',
        width: '10%',
        label: t(dsar_messages.cases.table.status),
        get: dsarCase => {
          const dueDate = dsarCase.dueDate ? new Date(dsarCase.dueDate) : null;
          const isOverdue = Boolean(dueDate && isPast(dueDate) && !['completed', 'closed'].includes(dsarCase.status.code));
          const statusLabel = isOverdue ? `${dsarCase.status.name}, ${t(dsar_messages.cases.table.overdue)}` : dsarCase.status.name;
          return (
            <div aria-label={statusLabel} className={styles.statusCell}>
              {getStatusIcon(dsarCase.status.code, isOverdue, styles)}
              <span>{statusLabel}</span>
            </div>
          );
        },
      },
      {
        id: 'identityVerificationStatus',
        width: '10%',
        label: t(dsar_messages.cases.table.identity_verification),
        get: dsarCase => dsarCase.identityVerificationStatus.name,
      },
      {
        id: 'receivedAt',
        width: '8%',
        label: t(dsar_messages.cases.table.received_at),
        get: dsarCase => <span className={styles.noWrap}>{formatDate(dsarCase.receivedAt)}</span>,
      },
      {
        id: 'dueDate',
        width: '8%',
        label: t(dsar_messages.cases.table.due_date),
        get: dsarCase => {
          const dueDate = dsarCase.dueDate ? new Date(dsarCase.dueDate) : null;
          const isOverdue = Boolean(dueDate && isPast(dueDate) && !['completed', 'closed'].includes(dsarCase.status.code));
          const dueDateLabel = isOverdue
            ? `${t(dsar_messages.cases.table.due_date)} ${formatDate(dsarCase.dueDate)}, ${t(dsar_messages.cases.table.overdue)}`
            : `${t(dsar_messages.cases.table.due_date)} ${formatDate(dsarCase.dueDate)}`;
          return (
            <Typography aria-label={dueDateLabel} className={styles.noWrap} color={isOverdue ? 'error' : 'initial'} variant='body2'>
              {formatDate(dsarCase.dueDate)}
            </Typography>
          );
        },
      },
      {
        id: 'distributedAt',
        width: '7%',
        label: t(dsar_messages.cases.table.distributed),
        get: dsarCase => <span className={styles.noWrap}>{formatDate(dsarCase.distributedAt)}</span>,
      },
      {
        id: 'createdAt',
        width: '7%',
        label: t(dsar_messages.cases.table.created_at),
        get: dsarCase => <span className={styles.noWrap}>{formatDate(dsarCase.createdAt)}</span>,
      },
      {
        id: 'actions',
        width: '6%',
        blockSorting: true,
        isAddon: true,
        label: t(dsar_messages.cases.table.actions),
        get: dsarCase => (
          <div className={styles.actionsCell}>
            <ContextMenu
              elements={[
                { label: t(dsar_messages.cases.actions.open), onClick: () => onOpen(dsarCase) },
                { label: t(dsar_messages.cases.actions.edit), onClick: () => onEdit(dsarCase) },
              ]}
              id={`dsar-case-${dsarCase.id}`}
            >
              <IconButton size='small'>
                <MoreIcon />
              </IconButton>
            </ContextMenu>
          </div>
        ),
      },
    ],
    [onEdit, onOpen, styles, t],
  );

  return (
    <>
      <div className={styles.searchBarOuterWrapper}>
        <div className={styles.searchInputCell}>
          <InputWithDebouncedAction
            action={onSearch}
            formControl={{ size: 'small' }}
            label={t(search_messages.input_label)}
            value={searchValue}
          />
        </div>
      </div>
      <div aria-busy={loading} aria-label={t(dsar_messages.cases.table.aria_label)} className={styles.tableScrollWrapper} role='region'>
        <Table
          clickableRow
          data={data}
          dataMapping={dataMapping}
          filtersService={filtersService}
          getRowHref={getRowHref}
          onSort={onSort}
          refetching={loading}
          sort={sort}
        />
      </div>
    </>
  );
};

export default DsarCasesTable;
